import { useState, useEffect, useRef } from "react";
import { Link, Navigate, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import AuthLayout from "../components/auth/AuthLayout";
import OtpInput from "../components/auth/OtpInput";
import TurnstileWidget, { type TurnstileWidgetRef } from "../components/auth/TurnstileWidget";
import { verifyEmail, resendOtp } from "../api/auth";
import { useAuth } from "../auth/AuthContext";

function maskEmail(email: string): string {
  if (!email || !email.includes("@")) return "your email";
  const [local, domain] = email.split("@");
  if (local.length <= 2) {
    return `${local[0]}***@${domain}`;
  }
  return `${local[0]}***${local[local.length - 1]}@${domain}`;
}

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const queryEmail = searchParams.get("email") || "";
  const locationState = location.state as { email?: string; message?: string } | null;
  const targetEmail = queryEmail || locationState?.email || "";

  const [otp, setOtp] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(60);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState(
    locationState?.message || "Please enter the 6-digit verification code sent to your email."
  );

  const turnstileRef = useRef<TurnstileWidgetRef>(null);

  // 60-second cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleVerify = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!targetEmail) {
      setError("Email address is missing. Please return to the registration page.");
      return;
    }
    if (otp.length !== 6) {
      setError("Please enter the complete 6-digit verification code.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await verifyEmail({
        email: targetEmail.trim().toLowerCase(),
        otp: otp.trim(),
      });

      setSuccessMsg("Email successfully verified! Redirecting to dashboard...");

      // If tokens returned, store session and redirect to dashboard
      if (res.access_token) {
        localStorage.setItem("access_token", res.access_token);
        localStorage.setItem("role", res.role || "client");
        await refreshUser();
        setTimeout(() => {
          if (res.role === "admin" || res.role === "super_admin") {
            navigate("/admin");
          } else {
            navigate("/dashboard");
          }
        }, 1200);
      } else {
        // Otherwise redirect to login
        setTimeout(() => {
          navigate("/login", {
            state: { message: "Email verified successfully! You can now sign in." },
          });
        }, 1200);
      }
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: {
          data?: {
            detail?: string;
          };
        };
        message?: string;
      };
      setError(axiosErr.response?.data?.detail || axiosErr.message || "Verification failed. The code may be invalid or expired.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (cooldown > 0 || resending) return;
    if (!targetEmail) {
      setError("Email address is missing. Please return to registration.");
      return;
    }

    setResending(true);
    setError("");

    try {
      await resendOtp({
        email: targetEmail.trim().toLowerCase(),
        turnstile_token: turnstileToken || undefined,
      });

      setSuccessMsg(`A new verification code has been dispatched to ${maskEmail(targetEmail)}.`);
      setCooldown(60);
      setOtp("");
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: {
          data?: {
            detail?: string;
          };
        };
        message?: string;
      };
      setError(axiosErr.response?.data?.detail || axiosErr.message || "Failed to resend verification code. Please try again later.");
    } finally {
      setResending(false);
    }
  };

  if (import.meta.env.VITE_LAUNCH_MODE === "true" || import.meta.env.VITE_ACCESS_REQUEST_MODE === "true") {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthLayout>
      <h2 className="tx-auth-form-title">Verify your email</h2>
      <p className="tx-auth-form-subtitle">
        We sent a 6-digit verification code to{" "}
        <strong style={{ color: "#ffffff" }}>{maskEmail(targetEmail)}</strong>. Enter the code below to activate your Threatactix account.
      </p>

      {successMsg && (
        <div className="tx-auth-alert tx-auth-alert-success" role="status">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <span>{successMsg}</span>
        </div>
      )}

      {error && (
        <div className="tx-auth-alert tx-auth-alert-error" role="alert">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <form className="tx-auth-form" onSubmit={handleVerify} noValidate>
        <div className="tx-auth-field">
          <label className="tx-auth-label">Enter 6-Digit Code</label>
          <OtpInput
            value={otp}
            onChange={(val) => {
              setOtp(val);
              if (error) setError("");
            }}
            length={6}
            disabled={loading}
            autoFocus
          />
        </div>

        <button
          type="submit"
          className="tx-auth-btn-primary"
          disabled={loading || otp.length !== 6}
          id="btn-verify-otp"
        >
          {loading ? (
            <>
              <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                <path d="M12 2a10 10 0 0 1 10 10" />
              </svg>
              <span>Verifying Code...</span>
            </>
          ) : (
            "Verify Email"
          )}
        </button>

        {/* Turnstile for Resend Protection (optional) */}
        {cooldown === 0 && (
          <TurnstileWidget
            ref={turnstileRef}
            onSuccess={(token) => setTurnstileToken(token)}
            onError={() => setTurnstileToken("")}
            onExpire={() => setTurnstileToken("")}
          />
        )}

        <div style={{ textAlign: "center", marginTop: 12 }}>
          {cooldown > 0 ? (
            <span style={{ fontSize: 13, color: "#64748b" }}>
              Resend code in <strong style={{ color: "#94a3b8" }}>{cooldown}s</strong>
            </span>
          ) : (
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={resending}
              style={{
                background: "none",
                border: "none",
                color: "#10b981",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                padding: "4px 8px",
              }}
              id="btn-resend-otp"
            >
              {resending ? "Sending..." : "Didn't receive code? Resend Code"}
            </button>
          )}
        </div>

        <div className="tx-auth-card-footer" style={{ marginTop: 18 }}>
          Wrong email address?{" "}
          <Link to="/register" className="tx-auth-link">
            Return to Sign Up
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
